import React, { useState } from 'react';
import Head from 'next/head';
import { styled, css, Box, Button, Container, Flex, Icon, Row, Column, Text } from '@goodrx/matisse';

import { useSynapse } from 'app/lib/synaptik';
import Transition from 'react-transition-group/Transition';
import { CoreLink, CoreLinkProps } from 'app/components/core/CoreLink/CoreLink';
import { MaintenanceBanner } from 'app/components/MaintenanceMode';
import { HeaderTheme } from './HeaderTheme';
import { PrimaryMarketingNav } from '../PrimaryMarketingNav';

const HEADER_MOBILE_BREAKPOINT = 800;

interface MarketingHeaderContainerProps {
  headerTheme?: HeaderTheme;
  isMobileNavOpen?: boolean;
}

const FixedContainer = styled(Box)`
  position: sticky;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
`;

const MarketingHeaderContainer = styled.header<MarketingHeaderContainerProps>`
  ${({ theme, headerTheme, isMobileNavOpen }) => {
    const isLightTheme = headerTheme === 'light' || isMobileNavOpen;
    const linkColor = isLightTheme ? theme.colors.greyDarkest : 'white';
    const backgroundColor = isLightTheme ? 'white' : theme.colors.primary;
    const borderColor = isLightTheme ? theme.colors.greyLight : 'transparent';
    const buttonColor = isLightTheme ? theme.colors.primary : 'white';

    return () => css`
      transition-duration: 0.3s;
      background: ${backgroundColor};
      border-bottom: 1px solid ${borderColor};

      *,
      a {
        color: ${linkColor};
      }

      #desktop-register-btn {
        border-color: ${buttonColor};
        color: ${theme.colors.primary};
        background-color: ${theme.colors.white};
      }
    `;
  }};
`;

const HeaderLogo = styled.img`
  height: 30px;
  width: auto;
  opacity: 0.9999; /* Chrome SVG Pixellation: https://stackoverflow.com/a/45911373 */
  box-sizing: border-box;
  margin-right: 24px;
`;

const ShowOnMobile = styled.div`
  display: none;

  @media (max-width: ${HEADER_MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const ShowOnDesktop = styled.div`
  display: block;

  @media (max-width: ${HEADER_MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const DotContainer = styled(Box)`
  font-size: 12px;
`;

const NavLink = ({ children, ...props }: CoreLinkProps) => {
  return (
    <Box mr={4}>
      <CoreLink {...props}>
        <span>{children}</span>
      </CoreLink>
    </Box>
  );
};

interface DesktopNavProps {
  reauthenticating: boolean;
  isAuthenticated: boolean;
  headerTheme?: HeaderTheme;
}

const DesktopNav = ({ reauthenticating, isAuthenticated, headerTheme }: DesktopNavProps) => {
  return (
    <Flex ml="auto" alignItems="center">
      <PrimaryMarketingNav href="/services" headerTheme={headerTheme}>
        Services
      </PrimaryMarketingNav>
      <PrimaryMarketingNav href="/learn" headerTheme={headerTheme}>
        Learn
      </PrimaryMarketingNav>
      <PrimaryMarketingNav href="/about" headerTheme={headerTheme}>
        About
      </PrimaryMarketingNav>
      <PrimaryMarketingNav
        href="https://help.heydoctor.com"
        target="_blank"
        rel="noopener noreferrer"
        headerTheme={headerTheme}
        absolute>
        Help
      </PrimaryMarketingNav>
      <PrimaryMarketingNav href="tel:1-415-237-1378" headerTheme={headerTheme} absolute>
        (415) 237-1378
      </PrimaryMarketingNav>

      <DotContainer ml={0} mr={4}>
        •
      </DotContainer>

      {reauthenticating ? null : isAuthenticated ? (
        <CoreLink href="/account">
          <Button id="desktop-register-btn" variant="secondary" size="sm">
            Go To Account
          </Button>
        </CoreLink>
      ) : (
        <>
          <Box mr={3}>
            <CoreLink href="/login">
              <Text color={headerTheme === 'dark' ? 'white' : 'primary'}>Sign In</Text>
            </CoreLink>
          </Box>

          <CoreLink href="/register">
            <Button id="desktop-register-btn" variant="secondary" size="sm">
              Get Started
            </Button>
          </CoreLink>
        </>
      )}
    </Flex>
  );
};

const defaultStyle = {
  transition: `all 125ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 0.5 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

const MobileNavLink = (props: CoreLinkProps) => (
  <Box style={{ fontSize: 24 }} mb={24}>
    <NavLink {...props} />
  </Box>
);

function MobileNav({ isOpen, isAuthenticated }: { isOpen: boolean; isAuthenticated?: boolean }) {
  return (
    <Transition in={isOpen} timeout={1}>
      {(state) => (
        <Box
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
          }}>
          <Row mt={3}>
            <Column xs={6}>
              <MobileNavLink href="/services">Services</MobileNavLink>
              <MobileNavLink href="/learn">Learn</MobileNavLink>
              <MobileNavLink href="/about">About</MobileNavLink>
            </Column>
            <Column xs={6}>
              <MobileNavLink absolute href="https://help.heydoctor.com">
                Help Center
              </MobileNavLink>
              <MobileNavLink absolute href="tel:1-415-237-1378">
                Call Us
              </MobileNavLink>
            </Column>
          </Row>

          <Row gutter={12}>
            {!isAuthenticated ? (
              <Column xs={6}>
                <CoreLink href="/login">
                  <Button variant="secondary" block>
                    Sign In
                  </Button>
                </CoreLink>
              </Column>
            ) : (
              <></>
            )}

            {!isAuthenticated ? (
              <Column xs={6}>
                <CoreLink href="/register">
                  <Button block style={{ color: 'white' }}>
                    Get Started
                  </Button>
                </CoreLink>
              </Column>
            ) : (
              <></>
            )}

            {isAuthenticated ? (
              <Column xs={6}>
                <CoreLink href="/account">
                  <Button block style={{ color: 'white' }}>
                    Go To Account
                  </Button>
                </CoreLink>
              </Column>
            ) : (
              <></>
            )}
          </Row>

          <Box mb={24} />
        </Box>
      )}
    </Transition>
  );
}

interface MarketingHeaderProps {
  headerTheme?: HeaderTheme;
}

export const MarketingHeader: React.FC<MarketingHeaderProps> = ({ headerTheme = 'light' }: MarketingHeaderProps) => {
  const [initialHeaderTheme] = useState(headerTheme);
  const [currentHeaderTheme, setCurrentHeaderTheme] = useState(headerTheme);

  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [reauthenticating, isAuthenticated] = useSynapse(({ user }) => [
    !!user?.state?.reauthenticating,
    user?.state?.authenticated,
  ]);

  const toggleMobileNav = () => setMobileNavOpen(!isMobileNavOpen);

  React.useEffect(() => {
    const handleThemeChange = () => {
      if (initialHeaderTheme === 'light') {
        return;
      }

      const pageOffset = window.pageYOffset;
      if (pageOffset > 40) {
        setCurrentHeaderTheme('light');
      } else if (pageOffset <= 40) {
        setCurrentHeaderTheme(initialHeaderTheme);
      }
    };

    window.addEventListener('scroll', handleThemeChange);
    return () => window.removeEventListener('scroll', handleThemeChange);
  }, [initialHeaderTheme, currentHeaderTheme]);

  const image = currentHeaderTheme === 'light' || isMobileNavOpen ? '-wordmark-dark' : '.landscape';

  return (
    <FixedContainer>
      <MaintenanceBanner />
      <MarketingHeaderContainer
        headerTheme={currentHeaderTheme}
        isMobileNavOpen={isMobileNavOpen}
        style={{ overflow: 'hidden', height: isMobileNavOpen ? 'auto' : 60 }}>
        <Container>
          <Flex alignItems="center" justifyContent="space-between" style={{ height: 60 }}>
            <CoreLink href="/" style={{ display: 'inherit' }}>
              <HeaderLogo alt="HeyDoctor Logo" src={`https://cdn.heydoctor.com/images/logos/heydoctor${image}.svg`} />
            </CoreLink>

            <ShowOnMobile>
              <Flex ml="auto" alignItems="center">
                <Icon
                  name={isMobileNavOpen ? 'close' : 'menu'}
                  color={currentHeaderTheme === 'light' || isMobileNavOpen ? 'greyDarkest' : 'white'}
                  size={32}
                  onClick={toggleMobileNav}
                />
              </Flex>
            </ShowOnMobile>

            <ShowOnDesktop data-qa="header-nav-desktop">
              <DesktopNav
                headerTheme={currentHeaderTheme}
                reauthenticating={reauthenticating}
                isAuthenticated={isAuthenticated}
              />
            </ShowOnDesktop>
          </Flex>

          <ShowOnMobile data-qa="header-nav-mobile">
            <MobileNav isOpen={isMobileNavOpen} isAuthenticated={isAuthenticated} />
            {isMobileNavOpen && (
              <Head>
                <style type="text/css">
                  {`
                    main {
                      opacity: 0.1;
                      pointer-events: none;
                    }
                  `}
                </style>
              </Head>
            )}
          </ShowOnMobile>
        </Container>
      </MarketingHeaderContainer>
    </FixedContainer>
  );
};
